/**
 * Known-issue banner — shows admin-written service notices at the top of every
 * screen (apps/api modules/service-notices).
 *
 * Polled every 2 minutes. Each notice can be hidden by the person reading it;
 * the dismissal is remembered against the notice's id AND its updatedAt, so an
 * admin editing the notice (new workaround, wider impact) shows it again.
 * A resolved notice stays for 24 hours in green, so people see it is over.
 */

import { useEffect, useState } from 'react';
import { getServiceNotices } from '@/api/endpoints/serviceNotices.js';

const POLL_MS = 2 * 60 * 1000;
const STORAGE_KEY = 'omni.serviceNotices.dismissed';

const TONE = {
  outage: { icon: 'fa-circle-exclamation', colour: 'var(--bad, #dc2626)', label: 'Outage' },
  warning: { icon: 'fa-triangle-exclamation', colour: 'var(--warn, #d97706)', label: 'Known issue' },
  info: { icon: 'fa-circle-info', colour: 'var(--info, #2563eb)', label: 'Notice' },
  resolved: { icon: 'fa-circle-check', colour: 'var(--ok, #059669)', label: 'Resolved' }
};

function readDismissed() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}
function writeDismissed(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* private window etc. — the banner simply comes back next load */
  }
}

function when(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export default function ServiceNoticeBanner() {
  const [notices, setNotices] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getServiceNotices()
        .then((r) => {
          if (!cancelled) setNotices(Array.isArray(r?.notices) ? r.notices : []);
        })
        .catch(() => {
          /* keep the last good list; a failed poll must not hide a known issue */
        });
    load();
    const t = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('omni:service-notices-changed', onFocus);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('omni:service-notices-changed', onFocus);
    };
  }, []);

  const visible = notices.filter((n) => dismissed[n.id] !== `${n.updatedAt}|${n.status}`);
  if (!visible.length) return null;

  function dismiss(n) {
    const next = { ...readDismissed(), [n.id]: `${n.updatedAt}|${n.status}` };
    // Forget dismissals for notices that no longer exist.
    const live = new Set(notices.map((x) => x.id));
    for (const k of Object.keys(next)) if (!live.has(k)) delete next[k];
    writeDismissed(next);
    setDismissed(next);
  }

  return (
    <div className="service-notices" role="region" aria-label="Known issues">
      {visible.map((n) => {
        const tone = n.status === 'resolved' ? TONE.resolved : TONE[n.severity] ?? TONE.warning;
        const open = expanded[n.id] ?? n.status === 'active';
        return (
          <div key={n.id} className="service-notice" style={{ '--sn-colour': tone.colour }}>
            <i className={`fas ${tone.icon} service-notice__icon`} aria-hidden="true" />
            <div className="service-notice__body">
              <div className="service-notice__head">
                <span className="service-notice__tag">{tone.label}</span>
                <strong className="service-notice__title">{n.title}</strong>
                <span className="service-notice__when">
                  {n.status === 'resolved' ? `fixed ${when(n.resolvedAt)}` : `since ${when(n.createdAt)}`}
                </span>
                <button
                  type="button"
                  className="service-notice__toggle"
                  onClick={() => setExpanded((e) => ({ ...e, [n.id]: !open }))}
                  aria-expanded={open}
                >
                  {open ? 'Less' : 'Details'}
                </button>
              </div>
              {open && (
                <div className="service-notice__detail">
                  {n.status === 'resolved' && n.resolutionNote ? <p>{n.resolutionNote}</p> : <p>{n.message}</p>}
                  {n.status === 'active' && n.affects?.length > 0 && (
                    <div className="service-notice__affects">
                      <span>Affects:</span>
                      {n.affects.map((a) => (
                        <span key={a} className="service-notice__chip">{a}</span>
                      ))}
                    </div>
                  )}
                  {n.status === 'active' && n.workaround && (
                    <p className="service-notice__workaround">
                      <strong>What to do instead:</strong> {n.workaround}
                    </p>
                  )}
                </div>
              )}
            </div>
            <button type="button" className="service-notice__close" onClick={() => dismiss(n)} title="Hide this notice" aria-label="Hide this notice">
              <i className="fas fa-xmark" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
